import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, extractError } from "../lib/api";
import { groupedResponseToMap, optionsForGroup } from "../lib/lookups";
import { useAuth } from "./AuthContext";
import { useWSEvent } from "./WebSocketContext";
import { toast } from "sonner";

const LookupOptionsContext = createContext(null);

export function LookupOptionsProvider({ children }) {
  const { user, lookupOptions, setLookupMapFromResponse } = useAuth();
  const [lookupMap, setLookupMap] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) {
      setLookupMap(null);
      return;
    }
    if (lookupOptions) {
      setLookupMap(groupedResponseToMap(lookupOptions));
    }
  }, [user, lookupOptions]);

  const reload = useCallback(async () => {
    if (!user) {
      setLookupMap(null);
      return;
    }
    setLoading(true);
    try {
      const { data: activeData } = await api.get("/lookup-options");
      setLookupMap(groupedResponseToMap(activeData));
      setLookupMapFromResponse?.(activeData);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  }, [user, setLookupMapFromResponse]);

  useWSEvent("lookup", reload);

  const optionsFor = useCallback(
    (groupKey, opts) => optionsForGroup(lookupMap, groupKey, opts),
    [lookupMap],
  );

  const value = useMemo(
    () => ({ lookupMap, loading, reload, optionsFor }),
    [lookupMap, loading, reload, optionsFor],
  );

  return <LookupOptionsContext.Provider value={value}>{children}</LookupOptionsContext.Provider>;
}

export function useLookups() {
  const ctx = useContext(LookupOptionsContext);
  if (!ctx) {
    return {
      lookupMap: null,
      loading: false,
      reload: async () => {},
      optionsFor: (groupKey, opts) => optionsForGroup(null, groupKey, opts),
    };
  }
  return ctx;
}
